class AlgoliaProductHit extends HTMLElement {
  constructor() {
    super();
    // this.attachShadow({ mode: 'open' });
    this.MAX_SPECIALTIES = {
      DESKTOP: 3,
      MOBILE: 3
    };
    this.MAX_AMBIANCE = {
      DESKTOP: 1,
      MOBILE: 1
    };
    this.BASE_CDN_URL = "https://cdn.shopify.com/s/files/1/0729/5909/9129/files";
    this.BY_THE_GLASS_LOGO = `${this.BASE_CDN_URL}/coravin-by-the-glass-rating.png?v=1749057912`;

    this.SHOPIFY_THEME_SETTINGS = this._getThemeSettingsData();
  }

  static get observedAttributes() {
    return ['hit', 'hit-image', 'components', 'track-search-attribution'];
  }

  connectedCallback() {
    this.render();
  }

  attributeChangedCallback() {
    this.render();
  }

  render() {
    const hit = JSON.parse(this.getAttribute('hit') || '{}');
    const hitImage = JSON.parse(this.getAttribute('hit-image') || '{}');
    const components = JSON.parse(this.getAttribute('components') || '{}');
    const trackSearchAttributionStr = this.getAttribute('track-search-attribution');

    // Convert attributes to functions
    const trackSearchAttribution = trackSearchAttributionStr ? new Function('hit', trackSearchAttributionStr) : null;
    Object.keys(components).forEach((k) => {
      if (components[k]) {
        components[k] = new Function('hit', components[k]);
      }
    });

    // Get relevant venue properties
    const rating = hit.meta?.custom?.rating;
    const venueType = hit?.meta?.custom?.venue_type ?? "";
    const wineSpecialties = hit.meta?.custom?.wine_specialty ?? [];
    const wineVenueAmbiance = hit.meta?.custom?.wine_venue_ambiance ?? [];
    const tags = hit.tags ?? [];
    const badgePrefix = "_badge_";
    let venueBadge = null;
    if(tags.length){
      const mainBadge = tags.find((t) => t.includes(badgePrefix));
      if(mainBadge){
        venueBadge = mainBadge.replace(badgePrefix, "").replace("_", " ");
      }
    }

    this.innerHTML = `
     <div
        class="ais-hit ais-product"
        data-algolia-index="${hit.index}"
        data-algolia-position="${hit.productPosition}"
        data-algolia-queryid="${hit.queryID}"
        data-algolia-objectid="${hit.objectID}"
        data-handle="${hit.handle}"
        data-variant-id="${hit.objectID}"
        data-distinct="${hit._distinct}"
        data-product-id="${hit.id}"
        onClick="${() => trackSearchAttribution(hit)}"
      >
      <div class="ais-hit--picture-container">
       ${this._renderBadges({ hit, venueBadge })}
        <img
          class="ais-hit--picture"
          src="${hitImage.src}"
          srcset="${hitImage.srcset}"
          sizes="${hitImage.sizes}"
          alt="${hit.title} - ${hit.variant_title}"
          loading="lazy" 
        />
        
      </div>

        <div class="ais-hit--details">
        ${this._renderRating(rating, "", false)}
          <p class="ais-hit--title">
            <a
              data-algolia-index="${hit.index}"
              data-algolia-position="${hit.productPosition}"
              data-algolia-queryid="${hit.queryID}"
              data-algolia-objectid="${hit.objectID}"
              href="${algoliaShopify.helpers.instantsearchLink(hit)}"
              title="${algoliaShopify.helpers.fullTitle(
                  hit.title,
                  hit._distinct,
                )}"
            >
              ${hit.title}
            </a>
          </p>
          <p class='ais-hit--subtitle'>
              ${venueType} 
              ${hit.meta?.custom?.region_city ? `- ${hit.meta?.custom?.region_city}` : ''}
          </p>
          ${this._renderVenueAmbiance(wineVenueAmbiance)}
          ${this._renderSpecialties(wineSpecialties, "", false)}
        </div>
      </div>
    `;

    // Add click handler for search attribution
    if (trackSearchAttribution) {
      this.querySelector('.ais-hit').addEventListener('click', () => {
        const trackFn = new Function('hit', trackSearchAttribution);
        trackFn(hit);
      });
    }
  }

  _renderBadges({ hit, venueBadge = null } = {}){
    const badgeThemeSettings = this.SHOPIFY_THEME_SETTINGS?.badges ?? {};

    const isEventVenue = hit?.meta?.custom?.event_venue === true || hit?.meta?.custom?.event_venue === "true";
    const eventDateValue = hit?.meta?.custom?.event_date;
    const eventTextTimeValue = hit?.meta?.custom?.event_text_time;
    
    const hasEventTime = Boolean(
      isEventVenue &&
      eventTextTimeValue !== null &&
      eventTextTimeValue !== undefined &&
      String(eventTextTimeValue).trim() !== "" &&
      eventDateValue !== null &&
      eventDateValue !== undefined &&
      String(eventDateValue).trim() !== ""
    );

    // Default badge (from _badge_ tag)
    let primaryBadgeText = venueBadge;
    let primaryBadgeAlignment = badgeThemeSettings.badge_alignment ?? '';
    let primaryBadgeStyle = '';

    // Event badge overrides default badge
    if (isEventVenue && badgeThemeSettings.cs_item_event_badge) {
      primaryBadgeText = badgeThemeSettings.cs_item_event_badge;
      primaryBadgeAlignment = badgeThemeSettings.cs_badge_alignment ?? primaryBadgeAlignment;
      primaryBadgeStyle = `--text: var(--COLOR-CS-EVENT-BADGE-TEXT); --bg: var(--COLOR-CS-EVENT-BADGE-BG);`;
    }

    // Event time text badge overrides event badge when time exists
    if (hasEventTime) {
      primaryBadgeText = badgeThemeSettings.cs_item_event_time_text_badge;
      primaryBadgeAlignment =
        badgeThemeSettings.cs_event_time_text_badge_alignment ?? primaryBadgeAlignment;
      primaryBadgeStyle = `--text: var(--COLOR-CS-EVENT-TIME-TEXT-BADGE-TEXT); --bg: var(--COLOR-CS-EVENT-TIME-TEXT-BADGE-BG);`;
    }
  
    if (!primaryBadgeText) return '';

    const primaryBadgeHtml = `
      <span class="badge badge-box ${primaryBadgeAlignment}" ${primaryBadgeStyle ? `style="${primaryBadgeStyle}"` : ''}>
        ${primaryBadgeText}
      </span>
    `;

    if (!hasEventTime) return primaryBadgeHtml;

    const eventTimeBadgeAlignment = badgeThemeSettings.cs_event_time_badge_alignment ?? primaryBadgeAlignment;
    const formattedEventTime = this._formatEventTimeBadge(eventDateValue) + ' - ' + eventTextTimeValue;
    const eventTimeBadgeHtml = formattedEventTime ? `
      <span class="badge badge-box ${eventTimeBadgeAlignment} cs-event-time-badge"
        style="--text: var(--COLOR-CS-EVENT-TIME-BADGE-TEXT); --bg: var(--COLOR-CS-EVENT-TIME-BADGE-BG);">
        ${formattedEventTime}
      </span>
    ` : '';

    return `
      <div class="custom-badge-event-group--container">
        ${primaryBadgeHtml}
        ${eventTimeBadgeHtml}
      </div>
    `;
  }


  _formatEventTimeBadge(eventDateValue){
    if (!eventDateValue) return '';

    const dateValueStr = String(eventDateValue).trim();
    if (!dateValueStr) return '';

    const match = dateValueStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return dateValueStr;

    const yearNum = parseInt(match[1], 10);
    const dayNum = parseInt(match[3], 10);
    const monthIdx = parseInt(match[2], 10) - 1;

    if (
      !Number.isFinite(yearNum) ||
      !Number.isFinite(dayNum) ||
      !Number.isFinite(monthIdx) ||
      monthIdx < 0 ||
      monthIdx > 11
    ) {
      return dateValueStr;
    }

    const locale =
      document?.documentElement?.getAttribute?.('lang') ||
      navigator?.language ||
      'en';

    let month = '';
    try {
      const formatter = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' });
      month = formatter.format(new Date(Date.UTC(yearNum, monthIdx, dayNum, 12, 0, 0)));
    } catch (e) {
      const MONTHS_EN = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
      month = MONTHS_EN[monthIdx] || '';
    }

    if (!month) return dateValueStr;
    return `${dayNum} ${String(month).toUpperCase()}`;
  }

  _renderSpecialties(wineSpecialties = [], className = "", isDesktop = true) {
    if (!wineSpecialties?.length) return '';
    return `
        <div class="venue-hit--specialties ${className} ${!isDesktop ? 'on-top' : ''}">
          ${wineSpecialties
        .slice(0, isDesktop ? this.MAX_SPECIALTIES.DESKTOP : this.MAX_SPECIALTIES.MOBILE)
        .map((specialty) => `<span>${specialty}</span>`)
        .join('|')}
        </div>
      `;
  }


  _renderVenueAmbiance(wineVenueAmbiance = [], isDesktop = false){
    if (!wineVenueAmbiance?.length) return '';
    return `
        <div class="venue-hit--ambiance">
          ${wineVenueAmbiance
        .slice(0, isDesktop ? this.MAX_AMBIANCE.DESKTOP : this.MAX_AMBIANCE.MOBILE)
        .map((ambiance) => `<span>${ambiance}</span>`)
        .join('|')}
        </div>
      `;
  }

  _renderRating(rating = undefined, className = "", isDesktop=true){
    if(rating === undefined) return '';

    let btgImages = [];
    try{btgImages = JSON.parse(this.getAttribute("btg-images"))}catch(e){}
    const RATINGS_SRC = {}
    if(btgImages?.length){
      btgImages?.forEach((image, idx) => {
          if(image === "") return;
          RATINGS_SRC[idx + 1] = {
            src: `${this.BASE_CDN_URL}/${this._getFileNameFromShopifyPath(image)}`,
            maxWidth: idx == 0 ? "8px" : idx == 2 ? "18px": "15px"
          }
      })
    }
    if(RATINGS_SRC[1] === undefined) return '';
    
    // Render the rating image multiple times according to the rating (1, 2, 3)
    let html = '<div style="display: flex; gap: 2px;">';
    if(rating < 4){
    for(let i = 0; i < rating; i++) {
      html += `<img 
        class="venue-hit--rating-picture ${className}"
        src="${RATINGS_SRC[1].src}"
        style="max-width:10px; height: 20px; object-fit: contain;"
        loading="lazy"
        alt="Rating picture" 
      />`;
    }
    html += '</div>';
    return html;
  }
  // If rating is 4, render the rating image
  if(rating == 4){
    html += `<img 
      class="venue-hit--rating-picture ${className}"
      src="${RATINGS_SRC[4].src}"
      alt="Rating picture"
      style="max-width:15px; height: 20px; object-fit: contain;"
      loading="lazy" 
    />`;
    html += '</div>';
    return html;
  }
}

  _getFileNameFromShopifyPath(path){
    return path.replace("files/", "")
  }

  _getThemeSettingsData(){
    try{
      const scriptElement =
        document.getElementById('shopify-theme-settings-data') ||
        document.getElementById('shopify-theme-settings-data-algolia');
      if (scriptElement) {
        return JSON.parse(scriptElement.textContent);
      }
    }catch(e){
      console.warn("Failed to parse shopify theme settings data")
    }

    return {};
  }
}

customElements.define('algolia-product-hit', AlgoliaProductHit); 